/**
 * Client-side validation helpers for image upload flows.
 *
 * Limits here mirror the backend multer configuration:
 *  - POST /inspections/:id/photos            → MAX_FILE_SIZE (20 MB), 150 files per request
 *  - POST /inspections/batch-upload/:batch   → 20 MB, 100 files per request
 */

const MB = 1024 * 1024;

export const INSPECTION_PHOTO_LIMITS = {
  maxFiles: 150,
  maxFileSizeBytes: 20 * MB,
  extensions: ['.jpg', '.jpeg', '.png', '.gif'],
  mimeTypes: ['image/jpeg', 'image/png', 'image/gif'],
  itemLabel: 'photos',
  scopeLabel: 'upload',
};

export const BATCH_UPLOAD_LIMITS = {
  maxFiles: 100,
  maxFileSizeBytes: 20 * MB,
  extensions: ['.jpg', '.jpeg', '.png', '.tif', '.tiff', '.heic', '.heif'],
  mimeTypes: ['image/jpeg', 'image/png', 'image/tiff', 'image/heic', 'image/heif'],
  itemLabel: 'files',
  scopeLabel: 'batch',
};

export const REJECTION_REASONS = {
  UNSUPPORTED_FORMAT: 'unsupported-format',
  TOO_LARGE: 'too-large',
  TOO_MANY: 'too-many',
};

/**
 * Human-readable file size, e.g. "2.4 MB".
 */
export function formatFileSize(bytes) {
  if (bytes === null || bytes === undefined || Number.isNaN(Number(bytes))) return 'N/A';
  const value = Number(bytes);
  if (value < 1024) return `${value} B`;
  if (value < MB) return `${(value / 1024).toFixed(1)} KB`;
  if (value < 1024 * MB) return `${(value / MB).toFixed(1)} MB`;
  return `${(value / (1024 * MB)).toFixed(2)} GB`;
}

/**
 * Comma-separated, upper-case list of supported formats for display,
 * de-duplicated (jpg/jpeg → JPG, tif/tiff → TIFF, heic/heif → HEIC).
 */
export function formatExtensionList(limits) {
  const canonical = { jpeg: 'JPG', jpg: 'JPG', tif: 'TIFF', tiff: 'TIFF', heif: 'HEIC', heic: 'HEIC' };
  const seen = new Set();
  const out = [];
  (limits.extensions || []).forEach((ext) => {
    const key = ext.replace(/^\./, '').toLowerCase();
    const label = canonical[key] || key.toUpperCase();
    if (!seen.has(label)) {
      seen.add(label);
      out.push(label);
    }
  });
  return out.join(', ');
}

/**
 * The `accept` attribute value for a native <input type="file">.
 */
export function buildAcceptAttribute(limits) {
  return [...(limits.mimeTypes || []), ...(limits.extensions || [])].join(',');
}

/**
 * The `accept` option for react-dropzone ({ mime: [ext, ...] }).
 */
export function buildDropzoneAccept(limits) {
  const accept = {};
  (limits.mimeTypes || []).forEach((mime) => {
    accept[mime] = [];
  });
  // Dropzone wants extensions attached to a mime key; attach them all to the first key
  // (the extension fallback is what matters for files with an empty MIME type).
  const firstMime = (limits.mimeTypes || [])[0];
  if (firstMime) accept[firstMime] = [...(limits.extensions || [])];
  return accept;
}

function getExtension(name) {
  const match = /\.[^.]+$/.exec(String(name || '').toLowerCase());
  return match ? match[0] : '';
}

export function isSupportedFormat(file, limits) {
  const mime = String(file?.type || '').toLowerCase();
  const ext = getExtension(file?.name);
  const mimeOk = mime && limits.mimeTypes.includes(mime);
  const extOk = ext && limits.extensions.includes(ext);
  // Browsers frequently report an empty or generic MIME for HEIC/TIFF on Windows,
  // so accept when either check passes.
  return Boolean(mimeOk || extOk);
}

function fileKey(file) {
  return `${file.name}|${file.size}|${file.lastModified || 0}`;
}

/**
 * Validate a set of incoming files against the limits, taking already-selected
 * files into account for the cumulative count.
 *
 * @returns {{ accepted: File[], rejected: Array<{ file: File, reason: string }> }}
 */
export function validateFiles(incomingFiles, existingFiles, limits) {
  const accepted = [];
  const rejected = [];
  const existingKeys = new Set((existingFiles || []).map(fileKey));
  let remaining = Math.max(0, limits.maxFiles - (existingFiles || []).length);

  Array.from(incomingFiles || []).forEach((file) => {
    if (!file) return;
    if (existingKeys.has(fileKey(file))) return; // silently skip duplicates

    if (!isSupportedFormat(file, limits)) {
      rejected.push({ file, reason: REJECTION_REASONS.UNSUPPORTED_FORMAT });
      return;
    }
    if (file.size > limits.maxFileSizeBytes) {
      rejected.push({ file, reason: REJECTION_REASONS.TOO_LARGE });
      return;
    }
    if (remaining <= 0) {
      rejected.push({ file, reason: REJECTION_REASONS.TOO_MANY });
      return;
    }
    remaining -= 1;
    existingKeys.add(fileKey(file));
    accepted.push(file);
  });

  return { accepted, rejected };
}

/**
 * Convert react-dropzone fileRejections into our { file, reason } shape.
 */
export function fromDropzoneRejections(fileRejections) {
  return (fileRejections || []).map(({ file, errors }) => {
    const codes = (errors || []).map((e) => e.code);
    let reason = REJECTION_REASONS.UNSUPPORTED_FORMAT;
    if (codes.includes('file-too-large')) reason = REJECTION_REASONS.TOO_LARGE;
    else if (codes.includes('too-many-files')) reason = REJECTION_REASONS.TOO_MANY;
    return { file, reason };
  });
}

function listNames(items, formatter, max = 5) {
  const shown = items.slice(0, max).map(formatter);
  const extra = items.length - shown.length;
  return extra > 0 ? `${shown.join(', ')} and ${extra} more` : shown.join(', ');
}

/**
 * Group rejections into human-readable lines.
 * @returns {string[]}
 */
export function buildRejectionSummary(rejected, limits) {
  if (!rejected || rejected.length === 0) return [];

  const byReason = {
    [REJECTION_REASONS.UNSUPPORTED_FORMAT]: [],
    [REJECTION_REASONS.TOO_LARGE]: [],
    [REJECTION_REASONS.TOO_MANY]: [],
  };
  rejected.forEach((r) => {
    (byReason[r.reason] || byReason[REJECTION_REASONS.UNSUPPORTED_FORMAT]).push(r.file);
  });

  const lines = [];
  const unsupported = byReason[REJECTION_REASONS.UNSUPPORTED_FORMAT];
  if (unsupported.length) {
    lines.push(
      `Unsupported format (only ${formatExtensionList(limits)} allowed): ${listNames(unsupported, (f) => f.name)}`
    );
  }
  const tooLarge = byReason[REJECTION_REASONS.TOO_LARGE];
  if (tooLarge.length) {
    lines.push(
      `Larger than ${formatFileSize(limits.maxFileSizeBytes)}: ${listNames(tooLarge, (f) => `${f.name} (${formatFileSize(f.size)})`)}`
    );
  }
  const tooMany = byReason[REJECTION_REASONS.TOO_MANY];
  if (tooMany.length) {
    lines.push(
      `Maximum ${limits.maxFiles} ${limits.itemLabel} per ${limits.scopeLabel} reached: ${tooMany.length} ${tooMany.length === 1 ? 'file was' : 'files were'} not added`
    );
  }
  return lines;
}

/**
 * Short line describing the limits, for captions under a drop zone.
 */
export function describeLimits(limits) {
  return `${formatExtensionList(limits)} · up to ${formatFileSize(limits.maxFileSizeBytes)} each · max ${limits.maxFiles} ${limits.itemLabel} per ${limits.scopeLabel}`;
}

/**
 * Translate an axios error from an upload request into a user-friendly message.
 */
export function describeUploadError(err, limits, fallbackPrefix = 'Failed to upload') {
  const status = err?.response?.status;
  const serverMessage = String(
    err?.response?.data?.error || err?.response?.data?.message || ''
  );
  const lower = serverMessage.toLowerCase();

  if (status === 413) {
    return `The upload is too large for the server to accept at once. Try uploading fewer ${limits.itemLabel} at a time.`;
  }
  if (
    lower.includes('unexpected field') ||
    lower.includes('too many files') ||
    lower.includes('limit_file_count') ||
    lower.includes('limit_unexpected_file')
  ) {
    return `Maximum ${limits.maxFiles} ${limits.itemLabel} per ${limits.scopeLabel}. Remove some files and try again.`;
  }
  if (lower.includes('file too large')) {
    return serverMessage;
  }
  if (lower.includes('only image files')) {
    return `One or more files are not images. Only ${formatExtensionList(limits)} are supported.`;
  }
  if (!err?.response) {
    return 'Network error: the upload did not reach the server. Check your connection and try again.';
  }
  return `${fallbackPrefix}: ${status || ''}${serverMessage ? ` - ${serverMessage}` : ''}`.trim();
}
